import React, { useState } from "react";
import { fmtTime, overlaps, todayKey } from "../../bookingService";

/**
 * Edit a booking: customer details, resource, and date/time.
 * Conflicts are checked before saving; time-only tweaks can also
 * use the Move flow from the bookings list.
 */
export default function BookingEditModal({ booking, resources, bookings, onClose, onSave, saving }) {
    const [customer, setCustomer] = useState(booking.customer || "");
    const [email, setEmail] = useState(booking.email || "");
    const [phone, setPhone] = useState(booking.phone || "");
    const [type, setType] = useState(booking.type || "");
    const [notes, setNotes] = useState(booking.notes || "");
    const [resourceId, setResourceId] = useState(booking.resourceId || "");
    const [date, setDate] = useState(booking.date || todayKey());
    const [start, setStart] = useState(booking.start || "");
    const [end, setEnd] = useState(booking.end || "");
    const [formError, setFormError] = useState("");

    const myResources = (resources || []).filter((r) => r.ownerId === booking.ownerId);

    const handleSave = () => {
        if (!customer.trim()) {
            setFormError("Customer name is required.");
            return;
        }
        if (email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
            setFormError("Enter a valid email (or leave it blank).");
            return;
        }
        if (!/^\d{4}-\d{2}-\d{2}$/.test(date || "")) {
            setFormError("Pick a valid date.");
            return;
        }
        if (!/^\d{2}:\d{2}$/.test(start || "") || !/^\d{2}:\d{2}$/.test(end || "")) {
            setFormError("Pick valid start/end times.");
            return;
        }
        if (bookings && overlaps(bookings, resourceId, date, start, end, booking.id)) {
            const r = myResources.find((x) => x.id === resourceId);
            setFormError(`${r ? r.name : "That resource"} is already booked at that time.`);
            return;
        }
        setFormError("");
        onSave({
            customer: customer.trim(),
            email: email.trim(),
            phone: phone.trim(),
            type: type.trim() || booking.type,
            notes: notes.trim(),
            resourceId,
            date,
            start,
            end,
        });
    };

    return (
        <div className="modal show d-block" tabIndex="-1" style={{ background: "rgba(15,23,42,.45)" }}>
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content" style={{ borderRadius: 14, border: "none" }}>
                    <div className="modal-header">
                        <div>
                            <h5 className="modal-title hk-display font-bold">Edit {booking.id}</h5>
                            <div className="text-xs text-[#64748b]">{booking.date} · {fmtTime(booking.start)} – {fmtTime(booking.end)}</div>
                        </div>
                        <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        {formError && <div className="alert alert-danger py-2">{formError}</div>}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <div>
                                <label className="hk-label">Customer *</label>
                                <input className="hk-input" value={customer} onChange={(e) => setCustomer(e.target.value)} />
                            </div>
                            <div>
                                <label className="hk-label">Booking type</label>
                                <input className="hk-input" value={type} onChange={(e) => setType(e.target.value)} placeholder="Reservation" />
                            </div>
                            <div>
                                <label className="hk-label">Email</label>
                                <input className="hk-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
                            </div>
                            <div>
                                <label className="hk-label">Phone</label>
                                <input className="hk-input" value={phone} onChange={(e) => setPhone(e.target.value)} />
                            </div>
                            <div>
                                <label className="hk-label">Date</label>
                                <input className="hk-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="hk-label">Start</label>
                                    <input className="hk-input" type="time" value={start} onChange={(e) => setStart(e.target.value)} />
                                </div>
                                <div>
                                    <label className="hk-label">End</label>
                                    <input className="hk-input" type="time" value={end} onChange={(e) => setEnd(e.target.value)} />
                                </div>
                            </div>
                            <div className="sm:col-span-2">
                                <label className="hk-label">Resource</label>
                                <select className="hk-select" value={resourceId} onChange={(e) => setResourceId(e.target.value)}>
                                    {myResources.map((r) => (
                                        <option key={r.id} value={r.id}>{r.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="sm:col-span-2">
                                <label className="hk-label">Notes</label>
                                <textarea className="hk-input" rows="2" value={notes} onChange={(e) => setNotes(e.target.value)} />
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button className="hk-btn hk-btn-outline hk-btn-sm" onClick={onClose}>Cancel</button>
                        <button className="hk-btn hk-btn-primary hk-btn-sm" disabled={!!saving} onClick={handleSave}>
                            {saving ? "Saving…" : "Save changes"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
